import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const SOCIAL_PROOF_RESUME_DELAY = 1500;
const SOCIAL_PROOF_DEFAULT_SPEED = 48;
const SOCIAL_PROOF_LAYOUT_RETRY_MS = 120;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (content.firstElementChild?.children.length === 0) return;

    if (this.dataset.socialProofMarquee === 'true') {
      this.#setupSocialProofMarquee();
      return;
    }

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#isSocialProofMarquee) {
      this.#teardownSocialProofMarquee();
      return;
    }

    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #isSocialProofMarquee = false;

  #socialProofFrame = 0;

  #socialProofLastFrameTime = 0;

  #socialProofLoopWidth = 0;

  #socialProofScrollPosition = 0;

  #socialProofIsPaused = false;

  #socialProofResumeTimer = 0;

  #socialProofAutoplayRetryTimer = 0;

  #socialProofIsDragging = false;

  #socialProofPointerId = null;

  #socialProofPointerStartX = 0;

  #socialProofPointerStartScrollLeft = 0;

  /** @type {ResizeObserver | null} */
  #socialProofResizeObserver = null;

  /** @type {MediaQueryList | null} */
  #socialProofReducedMotionQuery = null;

  // Dubai's existing pause control wraps the unchanged German motion engine.
  #socialProofControls = null;
  #socialProofVisibilityObserver = null;
  #socialProofUserPaused = false;
  #socialProofVisible = true;

  #socialProofControlsAllowAutoplay() {
    return !this.#socialProofControls ||
      (!this.#socialProofUserPaused && this.#socialProofVisible && !document.hidden);
  }

  #setupSocialProofControls() {
    const toggle = this.querySelector('[data-client-toggle]');
    if (!toggle) return;
    this.#socialProofControls = new AbortController();
    const options = { signal: this.#socialProofControls.signal };
    toggle.addEventListener('click', () => {
      this.#socialProofUserPaused = !this.#socialProofUserPaused;
      this.#syncSocialProofControls();
    }, options);
    document.addEventListener('visibilitychange', this.#syncSocialProofControls, options);
    this.refs.wrapper.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) return;
      this.#pauseSocialProofAutoplay();
      this.#scheduleSocialProofResume();
    }, options);
    this.#socialProofVisible = false;
    this.#socialProofVisibilityObserver = new IntersectionObserver(([entry]) => {
      this.#socialProofVisible = entry.isIntersecting;
      this.#syncSocialProofControls();
    });
    this.#socialProofVisibilityObserver.observe(this);
    this.#syncSocialProofControls();
  }

  #syncSocialProofControls = () => {
    if (!this.#socialProofControls) return;
    const toggle = this.querySelector('[data-client-toggle]');
    const reduced = this.#socialProofReducedMotionQuery?.matches;
    const paused = this.#socialProofUserPaused || reduced;
    // Reduced-motion users retain native manual scrolling without an autoplay control.
    toggle.hidden = Boolean(reduced);
    toggle.setAttribute('aria-label', paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
    toggle.setAttribute('aria-pressed', String(this.#socialProofUserPaused));
    toggle.querySelector('[data-client-pause-icon]').hidden = Boolean(paused);
    toggle.querySelector('[data-client-play-icon]').hidden = !paused;
    this.dataset.paused = String(paused || !this.#socialProofVisible || document.hidden);
    if (paused || !this.#socialProofControlsAllowAutoplay()) this.#pauseSocialProofAutoplay();
    else this.#startSocialProofAutoplay();
  };

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  #setupSocialProofMarquee() {
    this.#isSocialProofMarquee = true;
    this.#socialProofReducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.#prepareSocialProofMarquee();

    this.#setupSocialProofControls();

    const { wrapper } = this.refs;
    wrapper.addEventListener('pointerdown', this.#handleSocialProofPointerDown);
    wrapper.addEventListener('pointermove', this.#handleSocialProofPointerMove);
    wrapper.addEventListener('pointerup', this.#handleSocialProofPointerUp);
    wrapper.addEventListener('pointercancel', this.#handleSocialProofPointerUp);
    wrapper.addEventListener('mousedown', this.#handleSocialProofIntentStart);
    wrapper.addEventListener('mouseup', this.#handleSocialProofIntentEnd);
    wrapper.addEventListener('touchstart', this.#handleSocialProofIntentStart, { passive: true });
    wrapper.addEventListener('touchend', this.#handleSocialProofIntentEnd);
    wrapper.addEventListener('touchcancel', this.#handleSocialProofIntentEnd);
    wrapper.addEventListener('wheel', this.#handleSocialProofWheel, { passive: true });
    wrapper.addEventListener('dragstart', this.#handleSocialProofDragStart);

    if (typeof this.#socialProofReducedMotionQuery.addEventListener === 'function') {
      this.#socialProofReducedMotionQuery.addEventListener('change', this.#handleSocialProofMotionChange);
    } else {
      this.#socialProofReducedMotionQuery.addListener(this.#handleSocialProofMotionChange);
    }

    this.#socialProofResizeObserver = new ResizeObserver(this.#handleSocialProofResize);
    this.#socialProofResizeObserver.observe(this);
    this.#socialProofResizeObserver.observe(this.refs.wrapper);

    this.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', this.#handleSocialProofResize, { once: true });
    });

    if (!this.#socialProofReducedMotionQuery.matches) {
      requestAnimationFrame(() => {
        this.#prepareSocialProofMarquee();
        this.#startSocialProofAutoplay();
      });
    }
  }

  #teardownSocialProofMarquee() {
    this.#stopSocialProofAutoplay();
    this.#socialProofControls?.abort();
    this.#socialProofControls = null;
    this.#socialProofVisibilityObserver?.disconnect();
    window.clearTimeout(this.#socialProofResumeTimer);
    window.clearTimeout(this.#socialProofAutoplayRetryTimer);

    const { wrapper } = this.refs;
    wrapper.removeEventListener('pointerdown', this.#handleSocialProofPointerDown);
    wrapper.removeEventListener('pointermove', this.#handleSocialProofPointerMove);
    wrapper.removeEventListener('pointerup', this.#handleSocialProofPointerUp);
    wrapper.removeEventListener('pointercancel', this.#handleSocialProofPointerUp);
    wrapper.removeEventListener('mousedown', this.#handleSocialProofIntentStart);
    wrapper.removeEventListener('mouseup', this.#handleSocialProofIntentEnd);
    wrapper.removeEventListener('touchstart', this.#handleSocialProofIntentStart);
    wrapper.removeEventListener('touchend', this.#handleSocialProofIntentEnd);
    wrapper.removeEventListener('touchcancel', this.#handleSocialProofIntentEnd);
    wrapper.removeEventListener('wheel', this.#handleSocialProofWheel);
    wrapper.removeEventListener('dragstart', this.#handleSocialProofDragStart);
    if (typeof this.#socialProofReducedMotionQuery?.removeEventListener === 'function') {
      this.#socialProofReducedMotionQuery.removeEventListener('change', this.#handleSocialProofMotionChange);
    } else {
      this.#socialProofReducedMotionQuery?.removeListener(this.#handleSocialProofMotionChange);
    }
    this.#socialProofResizeObserver?.disconnect();
  }

  #prepareSocialProofMarquee = () => {
    const { content, wrapper } = this.refs;
    const originalItems = /** @type {HTMLElement | null} */ (
      content.querySelector('.marquee__repeated-items:not([data-marquee-clone])')
    );

    if (!originalItems) return;

    const contentStyles = window.getComputedStyle(content);
    const contentGap = parseFloat(contentStyles.columnGap || contentStyles.gap || '0') || 0;
    const originalWidth = originalItems.getBoundingClientRect().width;

    if (originalWidth <= 0) return;

    const previousLoopWidth = this.#socialProofLoopWidth || 0;
    const currentPosition = this.#socialProofScrollPosition || wrapper.scrollLeft || 0;
    const previousOffset = previousLoopWidth > 0 ? this.#positiveModulo(currentPosition, previousLoopWidth) : 0;
    const nextLoopWidth = originalWidth + contentGap;

    this.#socialProofLoopWidth = nextLoopWidth;

    const requiredCopies = Math.max(6, Math.ceil((wrapper.clientWidth * 2) / this.#socialProofLoopWidth) + 3);
    const existingClones = Array.from(content.children).filter(
      (element) => element instanceof HTMLElement && element.dataset.marqueeClone === 'true'
    );

    for (let index = existingClones.length + 1; index < requiredCopies; index += 1) {
      const clone = /** @type {HTMLElement} */ (originalItems.cloneNode(true));
      clone.dataset.marqueeClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      [clone, ...clone.querySelectorAll('*')].forEach((element) => {
        element.removeAttribute('id');
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify')) {
            element.removeAttribute(attribute.name);
          }
        });
      });
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      content.appendChild(clone);
    }

    const layoutChanged = Math.abs(nextLoopWidth - previousLoopWidth) > 1;

    if (previousLoopWidth <= 0 || (layoutChanged && !this.#socialProofIsPaused && !this.#socialProofIsDragging)) {
      this.#socialProofScrollPosition = this.#socialProofLoopWidth + Math.min(previousOffset, this.#socialProofLoopWidth - 1);
      this.#applySocialProofScrollPosition();
    }
  };

  #startSocialProofAutoplay() {
    if (this.#socialProofFrame || this.#socialProofReducedMotionQuery?.matches || !this.#socialProofControlsAllowAutoplay()) return;

    this.#syncSocialProofScrollPositionFromDom();
    this.#prepareSocialProofMarquee();

    if (this.#socialProofLoopWidth <= 0 || this.refs.wrapper.scrollWidth <= this.refs.wrapper.clientWidth + 1) {
      this.#scheduleSocialProofAutoplayRetry();
      return;
    }

    window.clearTimeout(this.#socialProofAutoplayRetryTimer);
    this.#socialProofIsPaused = false;
    this.#normalizeSocialProofScroll();
    this.#socialProofLastFrameTime = performance.now();
    this.#socialProofFrame = requestAnimationFrame(this.#runSocialProofAutoplay);
  }

  #stopSocialProofAutoplay() {
    if (this.#socialProofFrame) {
      cancelAnimationFrame(this.#socialProofFrame);
      this.#socialProofFrame = 0;
    }
  }

  #pauseSocialProofAutoplay() {
    this.#socialProofIsPaused = true;
    this.#stopSocialProofAutoplay();
  }

  #scheduleSocialProofResume() {
    window.clearTimeout(this.#socialProofResumeTimer);

    if (this.#socialProofReducedMotionQuery?.matches) return;

    this.#socialProofResumeTimer = window.setTimeout(() => {
      this.#startSocialProofAutoplay();
    }, SOCIAL_PROOF_RESUME_DELAY);
  }

  #scheduleSocialProofAutoplayRetry() {
    window.clearTimeout(this.#socialProofAutoplayRetryTimer);

    if (this.#socialProofReducedMotionQuery?.matches) return;

    this.#socialProofAutoplayRetryTimer = window.setTimeout(() => {
      this.#startSocialProofAutoplay();
    }, SOCIAL_PROOF_LAYOUT_RETRY_MS);
  }

  #runSocialProofAutoplay = (currentTime) => {
    if (this.#socialProofIsPaused || this.#socialProofReducedMotionQuery?.matches || !this.#socialProofControlsAllowAutoplay()) {
      this.#socialProofFrame = 0;
      return;
    }

    const elapsedSeconds = Math.min((currentTime - this.#socialProofLastFrameTime) / 1000, 0.05);
    const speed = Number(this.dataset.socialProofSpeed) || SOCIAL_PROOF_DEFAULT_SPEED;

    this.#socialProofLastFrameTime = currentTime;
    this.#socialProofScrollPosition += speed * elapsedSeconds;
    this.#normalizeSocialProofScroll();

    this.#socialProofFrame = requestAnimationFrame(this.#runSocialProofAutoplay);
  };

  #syncSocialProofScrollPositionFromDom() {
    const scrollLeft = this.refs.wrapper.scrollLeft;

    if (Number.isFinite(scrollLeft)) {
      this.#socialProofScrollPosition = scrollLeft;
    }

    this.refs.content.style.transform = '';
  }

  #applySocialProofScrollPosition() {
    const { content, wrapper } = this.refs;
    const scrollLeft = Math.floor(this.#socialProofScrollPosition);
    const fractionalOffset = this.#socialProofScrollPosition - scrollLeft;

    wrapper.scrollLeft = scrollLeft;
    content.style.transform = fractionalOffset ? `translate3d(${-fractionalOffset}px, 0, 0)` : '';
  }

  #normalizeSocialProofScroll() {
    if (this.#socialProofLoopWidth <= 0) return;

    const { wrapper } = this.refs;
    const scrollMax = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
    const upperBoundary = Math.max(this.#socialProofLoopWidth * 2, scrollMax - this.#socialProofLoopWidth);

    while (
      this.#socialProofScrollPosition >= upperBoundary &&
      this.#socialProofScrollPosition - this.#socialProofLoopWidth > 0
    ) {
      this.#socialProofScrollPosition -= this.#socialProofLoopWidth;
    }

    while (this.#socialProofScrollPosition <= 0) {
      this.#socialProofScrollPosition += this.#socialProofLoopWidth;
    }

    this.#applySocialProofScrollPosition();
  }

  #positiveModulo(value, divisor) {
    return ((value % divisor) + divisor) % divisor;
  }

  #handleSocialProofResize = debounce(() => {
    this.#prepareSocialProofMarquee();
    if (!this.#socialProofFrame && !this.#socialProofIsPaused) {
      this.#startSocialProofAutoplay();
    }
  }, 150);

  #handleSocialProofPointerDown = (event) => {
    this.#syncSocialProofScrollPositionFromDom();
    this.#pauseSocialProofAutoplay();

    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    this.#socialProofIsDragging = true;
    this.#socialProofPointerId = event.pointerId;
    this.#socialProofPointerStartX = event.clientX;
    this.#socialProofPointerStartScrollLeft = this.#socialProofScrollPosition;
    this.refs.wrapper.classList.add('is-dragging');
    this.refs.wrapper.setPointerCapture(event.pointerId);
  };

  #handleSocialProofPointerMove = (event) => {
    if (!this.#socialProofIsDragging || event.pointerId !== this.#socialProofPointerId) return;

    event.preventDefault();
    this.#socialProofScrollPosition =
      this.#socialProofPointerStartScrollLeft - (event.clientX - this.#socialProofPointerStartX);
    this.#normalizeSocialProofScroll();
  };

  #handleSocialProofPointerUp = (event) => {
    if (this.#socialProofIsDragging && event.pointerId === this.#socialProofPointerId) {
      this.#socialProofIsDragging = false;
      this.#socialProofPointerId = null;
      this.refs.wrapper.classList.remove('is-dragging');

      if (this.refs.wrapper.hasPointerCapture(event.pointerId)) {
        this.refs.wrapper.releasePointerCapture(event.pointerId);
      }
    }

    this.#scheduleSocialProofResume();
  };

  #handleSocialProofIntentStart = () => {
    this.#syncSocialProofScrollPositionFromDom();
    this.#pauseSocialProofAutoplay();
  };

  #handleSocialProofIntentEnd = () => {
    this.#syncSocialProofScrollPositionFromDom();
    this.#scheduleSocialProofResume();
  };

  #handleSocialProofWheel = () => {
    this.#pauseSocialProofAutoplay();
    this.#scheduleSocialProofResume();
  };

  #handleSocialProofDragStart = (event) => {
    event.preventDefault();
  };

  #handleSocialProofMotionChange = () => {
    this.#syncSocialProofControls();
    if (this.#socialProofReducedMotionQuery?.matches) {
      this.#pauseSocialProofAutoplay();
      return;
    }

    this.#scheduleSocialProofResume();
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    const { content } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement ? content.firstElementChild.offsetWidth : 1;

    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
